import { ReactNode, useEffect, useState } from 'react';

import { useLogoutFunction } from '@propelauth/react';
import parse from 'html-react-parser';
import { marked } from 'marked';
import { useNavigate } from 'react-router-dom';

import { useUserContext } from '../../Contexts/User';
import { useAcceptTermsOfUse } from '@/api/mutations/useAcceptTermsOfUse';
import { useDownloadTermsOfUsePdf } from '@/api/mutations/useDownloadTermsOfUsePdf';
import useGetTermsOfUseContent from '@/api/queries/useGetTermsOfUseContent';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';

export function AcceptTerms() {
  const { user, isLoaded } = useUserContext();
  const navigate = useNavigate();
  const logoutFn = useLogoutFunction();

  const [hasAgreed, setHasAgreed] = useState(false);
  const [parsedTerms, setParsedTerms] = useState<ReactNode>(null);

  const termsRequired = user?.termsAcceptanceRequired === true;
  const { data: content, isLoading, isError } = useGetTermsOfUseContent(termsRequired);
  const { mutate: acceptTerms, isLoading: isAccepting, isSuccess: hasAccepted } = useAcceptTermsOfUse();
  const { mutate: downloadPdf, isLoading: isDownloadingPdf } = useDownloadTermsOfUsePdf();

  // SSO users / users who already accepted have no business here.
  useEffect(() => {
    if (isLoaded && !termsRequired) {
      navigate('/', { replace: true });
    }
  }, [isLoaded, termsRequired, navigate]);

  useEffect(() => {
    const parseTerms = async () => {
      if (content?.markdown) {
        const parsed = await marked.parse(content.markdown);
        setParsedTerms(parse(parsed));
      }
    };
    parseTerms();
  }, [content?.markdown]);

  if (!isLoaded || !termsRequired) {
    return null;
  }

  // This error shows when the feature flag is on, but the terms couldn't be fetched from S3
  if (isError) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-white p-8">
        <div className="max-w-lg text-center">
          <div className="flex flex-col items-center gap-10">
            <div>
              <h2 className="mb-6 font-serif text-3xl font-medium text-gray-900">Terms of use unavailable</h2>
              <div className="text-sm text-balance text-gray-600">
                As a non-SSO user, your organisation requires you to accept its terms of use before using the platform,
                but the terms could not be loaded. If this issue persists, contact{' '}
                <a className="text-blue-500 hover:underline" href="mailto:support@wexler.ai">
                  support@wexler.ai
                </a>
                .
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={() => logoutFn(true)}>Log out</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-gray-50 px-6 py-10 pb-9">
      {/* Scroll-driven fade at the bottom edge, revealed as you near the end of the content. */}
      <style>{`
        @property --terms-fade {
          syntax: '<length>';
          inherits: false;
          initial-value: 64px;
        }
        .terms-scroll-fade {
          --terms-fade: 64px;
          mask-image: linear-gradient(to bottom, #000 calc(100% - var(--terms-fade)), rgba(0, 0, 0, 0.3));
        }
        @supports (animation-timeline: scroll()) {
          .terms-scroll-fade {
            animation: terms-unfade linear both;
            animation-timeline: scroll(self);
            animation-range: 99% 100%;
          }
          @keyframes terms-unfade {
            to {
              --terms-fade: 0px;
            }
          }
        }
      `}</style>

      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-6">
        <div className="space-y-2.5 px-9">
          <h1 className="font-serif text-2xl font-medium text-gray-900">Terms of use</h1>
          <p className="px-px text-sm text-gray-500">
            As a non-SSO user, your organisation requires you to read and accept the following terms before using
            Wexler.
          </p>
        </div>

        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="inline-flex animate-pulse items-center gap-3 text-sm text-gray-500">
              <Icon className="lucide--loader animate-spin ease-in-out" /> <p>Loading terms...</p>
            </div>
          </div>
        ) : (
          <div className="max-h-full grow overflow-hidden rounded-lg border bg-white">
            <div className="terms-scroll-fade h-full overflow-y-auto px-9 py-4">
              <div className="prose prose-sm prose-headings:my-3.5 prose-h1:text-lg prose-h1:font-semibold prose-h2:text-base prose-h2:font-semibold prose-p:leading-normal prose-p:my-3 max-w-none">
                {parsedTerms}
              </div>
            </div>
          </div>
        )}

        <div className="px-9">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <Checkbox
                checked={hasAgreed}
                onCheckedChange={(checked) => setHasAgreed(checked === true)}
                disabled={isLoading}
                className="bg-white"
              />
              I have read and agree to the terms of use
            </label>

            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                onClick={() => acceptTerms()}
                disabled={!hasAgreed || isAccepting || hasAccepted}
              >
                {isAccepting && <Icon className="lucide--loader animate-spin ease-in-out" />}
                Accept and continue
              </Button>
              <Button variant="white" onClick={() => downloadPdf()} disabled={isDownloadingPdf}>
                <Icon className="lucide--download" />
                Download as PDF
              </Button>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              Alternatively, you can{' '}
              <Button variant="ghost" className="p-0 text-xs" onClick={() => logoutFn(true)}>
                log out
              </Button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
